/**
 * Checks that the server publishes this device's identity keys.
 *
 * The Rust crypto store remembers that it already uploaded the device keys
 * and never uploads them again. When the server side of the device is
 * recreated under the same device ID (a login that reuses the stored device
 * ID after the device was removed), the server has the device but no keys
 * for it. Nobody can encrypt to such a device, so every incoming message is
 * "Unable to decrypt" and outgoing encryption never completes, while the
 * local store still reports crypto as set up.
 */

export type OwnDeviceKeysStatus = 'published' | 'missing' | 'mismatch';

export interface KeysQueryResponse {
    device_keys?: Record<string, Record<string, {keys?: Record<string, string>} | undefined> | undefined>;
}

export function ownDeviceKeysStatus(
    response: KeysQueryResponse,
    userId: string,
    deviceId: string,
    ownEd25519: string,
): OwnDeviceKeysStatus {
    const published = response.device_keys?.[userId]?.[deviceId];
    if (!published) return 'missing';
    return published.keys?.[`ed25519:${deviceId}`] === ownEd25519 ? 'published' : 'mismatch';
}

export interface OwnDeviceKeysCheckDeps {
    queryOwnKeys(): Promise<KeysQueryResponse>;
    ownEd25519(): Promise<string>;
    wait(ms: number): Promise<void>;
}

/** Time for a pending first key upload to land before a second look. */
export const OWN_DEVICE_KEYS_RECHECK_MS = 3_000;

/**
 * 'unknown' when the server could not be asked: a network error must never
 * cost the user their encryption store.
 */
export async function checkOwnDeviceKeys(
    userId: string,
    deviceId: string,
    deps: OwnDeviceKeysCheckDeps,
    recheckDelayMs = OWN_DEVICE_KEYS_RECHECK_MS,
): Promise<OwnDeviceKeysStatus | 'unknown'> {
    let status: OwnDeviceKeysStatus = 'published';
    try {
        const ownEd25519 = await deps.ownEd25519();
        for (let attempt = 0; attempt < 2; attempt += 1) {
            if (attempt > 0) await deps.wait(recheckDelayMs);
            status = ownDeviceKeysStatus(await deps.queryOwnKeys(), userId, deviceId, ownEd25519);
            if (status === 'published') return status;
        }
    } catch {
        return 'unknown';
    }
    return status;
}
